import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { Project } from "../../types/projectTypes";
import { getActiveProjects } from "../../api/projectApi";
import { getFullRasReport } from "../../api/productionControlApi";
import { FullRasReportResponse } from "../../types/rasTypes";
import ExcelJS from "exceljs";
import logo from '../../assets/images/logoElecnor.png';
import moment from "moment";
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { es } from 'date-fns/locale/es';
import { getUserProject } from "../../api/userApi";
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";

registerLocale('es', es);

// --- FUNCIÓN DE FORMATEO (Quita el .0) ---
const formatVal = (value: number | string | undefined) => {
    if (value === undefined || value === null) return "0";
    const num = Number(value);
    // Si es entero (ej: 58.0), devuelve "58". Si tiene decimal real (ej: 1.5), devuelve "1.5"
    if (Number.isInteger(num)) {
        return num.toFixed(0);
    }
    return num.toFixed(1); // Mantiene 1 decimal para Km o valores fraccionarios
};

const Summary: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [reportData, setReportData] = useState<FullRasReportResponse | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userRole, setUserRole] = useState<string>("");

    const matrixHeaderStyle = {
        backgroundColor: "#78a1bf",
        color: "#004F86",
        fontWeight: "bold",
        borderRight: "1px solid #ccc",
        fontSize: "0.60rem",
        padding: "2px 1px",
        minWidth: "45px",
        maxWidth: "55px",
        lineHeight: "1.1",
        whiteSpace: "normal" as const,
        textAlign: "center" as const,
        verticalAlign: "bottom"
    };

    const activityColStyle = {
        position: "sticky" as const,
        left: 0,
        zIndex: 3,
        backgroundColor: "white",
        color: "#004F86",
        fontWeight: "bold",
        borderRight: "1px solid #ccc",
        borderBottom: "1px solid #ccc",
        fontSize: "0.70rem",
        padding: "4px 8px",
        maxWidth: "180px",
        minWidth: "180px",
        whiteSpace: "normal" as const,
        lineHeight: "1.2",
        textAlign: "center" as const,
        verticalAlign: "middle"
    };

    const cellBodyStyle = {
        borderRight: "1px solid #e0e0e0",
        borderBottom: "1px solid #e0e0e0",
        padding: "0px 0px",
        height: "35px",
        textAlign: "center" as const,
        verticalAlign: "middle",
        fontSize: "0.9rem",
        fontWeight: "bold",
        lineHeight: "1",
        cursor: "default"
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const userStr = localStorage.getItem("user");
            let role = "", rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
                setUserRole(role);
            }
            let projectsData: Project[] = [];
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }
            setProjects(projectsData.filter((p) => p.status === undefined || p.status === true));
        } catch (error) { console.error(error); setProjects([]); }
    };

    const handleDateChange = (date: Date | null) => {
        setReportData(null);
        if (date) {
            setSelectedDate(date);
            const m = moment(date);
            const start = m.clone().startOf("isoWeek");
            const end = m.clone().endOf("isoWeek").subtract(1, "days");
            const dates = [];
            for (let d = start; d.isSameOrBefore(end); d.add(1, "days")) dates.push(d.toDate());
            setHighlightedDates(dates);
        }
    };

    const handleFilter = async () => {
        if (!selectedDate || !selectedProject) {
            setAlertMessage("Por favor selecciona un proyecto y una fecha.");
            return;
        }
        setAlertMessage('');
        if (highlightedDates.length === 0) return;

        setLoading(true);
        try {
            const isoStartDate = highlightedDates[0].toISOString();
            const isoEndDate = highlightedDates[highlightedDates.length - 1].toISOString();
            const data = await getFullRasReport(selectedProject, isoStartDate, isoEndDate);
            setReportData(data);
        } catch (error) {
            console.error("Error al cargar reporte:", error);
            setAlertMessage("Ocurrió un error al cargar los datos.");
        } finally {
            setLoading(false);
        }
    };

    const handleProjectChange = (e: SelectChangeEvent<number>) => {
        setSelectedProject(Number(e.target.value));
        setReportData(null);
    };

    const exportToExcel = async () => {
        if (!reportData) return;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Resumen de Ejecución");

        try {
            const imageResponse = await fetch(logo);
            const imageBuffer = await imageResponse.arrayBuffer();
            const imageId = workbook.addImage({ buffer: imageBuffer, extension: 'png', });
            worksheet.addImage(imageId, { tl: { col: 0, row: 1 }, ext: { width: 160, height: 60 }, });
        } catch (e) { console.warn(e); }

        const projectName = projects.find((project) => project.id === selectedProject)?.name || "Proyecto Desconocido";
        worksheet.getCell('B2').value = `PROYECTO: ${projectName.toUpperCase()}`;
        worksheet.getCell('B3').value = `FECHA: ${new Date().toLocaleDateString()}`;

        worksheet.addRow([]);

        const headerLabels = reportData.towerColumns.map(col => col.towerNumber);
        const headerRow1 = worksheet.addRow(["Actividad", ...headerLabels]);

        headerRow1.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "78A1BF" }, };
        });

        reportData.rows.forEach((row) => {
            const excelRow = worksheet.addRow([
                row.name,
                ...reportData.towerColumns.map(col => {
                    const status = row.towerStatusMap[col.towerNumber];
                    return status || "";
                })
            ]);

            excelRow.eachCell((cell, colNumber) => {
                cell.alignment = { horizontal: "center" };
                if (colNumber > 1) {
                    if (cell.value === "X") cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "C6EFCE" } };
                    else if (cell.value === "S") cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2CC" } };
                }
            });
        });

        worksheet.addRow([]);
        const headerRow2 = worksheet.addRow([ "Actividad", "Unidad", "Ejecutadas anteriormente (X)", "Ejecutadas en el periodo (S)", "Total"]);
        headerRow2.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center" };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "78A1BF" }, };
        });

        reportData.rows.forEach((row) => {
            const dataRow = worksheet.addRow([
                row.name,
                row.unit,
                formatVal(row.previousExecuted),
                formatVal(row.currentExecuted),
                formatVal(row.totalProgress)
            ]);
            dataRow.getCell(3).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "C6EFCE" } };
            dataRow.getCell(4).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2CC" } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Resumen_RAS_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ p: 2, height: "100%", display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" marginBottom={2} fontWeight="bold" sx={{ color: 'black' }}>
                Informe RAS
            </Typography>

            <Grid container spacing={2} alignItems="center" >
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Proyecto</InputLabel>
                        <Select value={selectedProject || ''} label="Proyecto" onChange={handleProjectChange}>
                            {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </Select>
                        {alertMessage && <Typography color="error">{alertMessage}</Typography>}
                    </FormControl>
                </Grid>
                {selectedProject && (
                    <>
                        <Grid item xs={12} md={4}>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateChange}
                                highlightDates={[ { "react-datepicker__day--highlighted": highlightedDates }, ]}
                                locale="es"
                                calendarStartDay={1}
                                customInput={ <TextField label="Selecciona el día" variant="outlined" fullWidth size="small" /> }
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="contained" color="primary" onClick={handleFilter} sx={{ height: "40px" }}>Filtrar</Button>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>

            {loading && <Box display="flex" justifyContent="center" m={3} flexGrow={1}><CircularProgress /></Box>}

            {!loading && reportData && (
                <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', mt: 2 }}>

                    <Box mb={1} sx={{fontSize: '0.8rem'}}>
                        <b>Periodo: </b>
                        {moment(highlightedDates[0]).format("DD-MM-YYYY")} al {moment(highlightedDates[highlightedDates.length - 1]).format("DD-MM-YYYY")}
                    </Box>

                    {/* --- TABLA 1: MATRIZ VISUAL --- */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '60vh', border: '1px solid #ccc' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" style={{ ...matrixHeaderStyle, position: "sticky", left: 0, zIndex: 5, width: "120px", maxWidth: "120px", verticalAlign: "middle" }}>Actividad</TableCell>
                                    {reportData.towerColumns.map((col, index) => (
                                        <TableCell key={index} align="center" style={matrixHeaderStyle}>
                                            <Tooltip title={
                                                <div style={{fontSize: '10px'}}>
                                                    {col.forwardSpan && <div>Vano: {col.forwardSpan}</div>}
                                                    {col.vertexName && <div>Vértice: {col.vertexName}</div>}
                                                    {col.towerType && <div>Tipo: {col.towerType}</div>}
                                                    <div>N°: {col.towerNumber}</div>
                                                </div>
                                            } arrow>
                                                <div style={{cursor: 'pointer'}}>
                                                    {col.forwardSpan && <>{col.forwardSpan}<br /></>}
                                                    {col.vertexName && <>{col.vertexName}<br /></>}
                                                    {col.towerType && <>{col.towerType}<br /></>}
                                                    {col.towerNumber}
                                                </div>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.rows.map((row) => (
                                    <TableRow key={row.activityId} hover>
                                        <TableCell align="center" sx={activityColStyle}>
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                                <span>{row.name}</span>
                                                {row.subActivity && (
                                                    <span style={{ fontSize: "0.85em", color: "#444", marginTop: "2px", fontWeight: "normal" }}>
                                                        {row.subActivity}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        {reportData.towerColumns.map((col, index) => {
                                            const status = row.towerStatusMap[col.towerNumber];
                                            let bgColor = "transparent";
                                            if (status === "X") bgColor = "#C6EFCE";
                                            else if (status === "S") bgColor = "#FFF2CC";
                                            return (
                                                <TableCell key={index} align="center" sx={{ ...cellBodyStyle, backgroundColor: bgColor }}>
                                                    {status || ""}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* --- TABLA 2: RESUMEN (Formateada) --- */}
                    <Box sx={{ mt: 2, flexGrow: 0 }}>
                        <Typography variant="h6" marginBottom={1} sx={{fontSize: '1rem', fontWeight: 'bold'}}>Resumen de Ejecución</Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: '40vh', overflowY: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#78a1bf', color: "#004F86", fontWeight: 'bold', padding: '4px', fontSize: '0.7rem' }}>Actividad</TableCell>
                                        <TableCell sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#78a1bf', color: "#004F86", fontWeight: 'bold', padding: '4px', fontSize: '0.7rem' }}>Unidad</TableCell>
                                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#78a1bf', color: "#004F86", fontWeight: 'bold', padding: '4px', fontSize: '0.7rem' }}>Ejecutadas anteriormente (X)</TableCell>
                                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#78a1bf', color: "#004F86", fontWeight: 'bold', padding: '4px', fontSize: '0.7rem' }}>Ejecutadas en el periodo (S)</TableCell>
                                        <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#78a1bf', color: "#004F86", fontWeight: 'bold', padding: '4px', fontSize: '0.7rem' }}>Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.rows.map((row) => (
                                        <TableRow key={row.activityId} hover>
                                            <TableCell sx={{ padding: '2px 4px', fontSize: '0.7rem' }}>{row.name} {row.subActivity ? `(${row.subActivity})` : ""}</TableCell>
                                            <TableCell sx={{ padding: '2px 4px', fontSize: '0.7rem' }}>{row.unit}</TableCell>

                                            {/* USO DE FORMATVAL AQUÍ PARA QUITAR DECIMALES */}
                                            <TableCell align="center" sx={{ backgroundColor: '#C8E6C9', padding: '2px 4px', fontSize: '0.7rem' }}>
                                                {formatVal(row.previousExecuted)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ backgroundColor: '#FFF9C4', padding: '2px 4px', fontSize: '0.7rem' }}>
                                                {formatVal(row.currentExecuted)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ padding: '2px 4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                {formatVal(row.totalProgress)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="primary" onClick={exportToExcel} size="small" disabled={userRole === profiles[3].name}>Exportar informe</Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default Summary;